import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import { Icon } from "@iconify/react";
import { useToast } from "../../design-system/contexts/ToastContext";
import { isValidEmail, parseAuthError } from "../../utils/validation";

// Auth Components
import { AuthLayout, BackHeader, OTPInput } from "./components";
import "./Auth.css";

const ForgotPassword = () => {
  const { success, error: showError } = useToast();
  const navigate = useNavigate();

  const [state, setState] = useState({
    data: {
      email: "",
      inputOtp: "",
      password: "",
      confirmPassword: "",
    },
    receivedOtp: false,
    loading: false,
    consent: false,
    errors: {
      email: "",
      inputOtp: "",
      password: "",
      confirmPassword: "",
    },
    redirect: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [counter, setCounter] = useState(30);
  useEffect(() => {
    const timer =
      counter > 0 && setInterval(() => setCounter(counter - 1), 1000);
    return () => clearInterval(timer);
  }, [state.receivedOtp && counter]);

  const doValidate = () => {
    if (state.data.email === undefined || state.data.email === "") {
      setState({
        ...state,
        errors: {
          ...state.errors,
          email: "Please enter your email",
        },
      });
      return false;
    }

    // Use improved email validation
    if (!isValidEmail(state.data.email)) {
      setState({
        ...state,
        errors: {
          ...state.errors,
          email: "Please enter a valid email address",
        },
      });
      return false;
    }

    return true;
  };

  const validateOtpAndPassword = () => {
    if (!state.data.inputOtp || state.data.inputOtp === "") {
      setState({
        ...state,
        errors: {
          ...state.errors,
          inputOtp: "Please enter OTP",
        },
      });
      return false;
    }
    if (state.data.inputOtp.length !== 6) {
      setState({
        ...state,
        errors: {
          ...state.errors,
          inputOtp: "Please enter a valid OTP",
        },
      });
      return false;
    }
    if (state.data.password === undefined || state.data.password === "") {
      setState({
        ...state,
        errors: {
          ...state.errors,
          password: "Please enter your password",
        },
      });
      return false;
    }
    if (
      !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#$%^&/,><'":;|_~=`+=\-!]).{6,}$/.test(
        state.data.password
      )
    ) {
      setState({
        ...state,
        errors: {
          ...state.errors,
          password: "Password must contain a minimum of 6 characters, 1 uppercase, 1 lowercase, 1 number & 1 special character",
        },
      });
      return false;
    }

    if (
      state.data.confirmPassword !== state.data.password &&
      state.data.confirmPassword !== ""
    ) {
      setState({
        ...state,
        errors: {
          ...state.errors,
          confirmPassword: "Passwords do not match",
        },
      });
      return false;
    }

    if (
      state.data.confirmPassword === undefined ||
      state.data.confirmPassword === ""
    ) {
      setState({
        ...state,
        errors: {
          ...state.errors,
          confirmPassword: "Please confirm the password",
        },
      });
      return false;
    }
    return true;
  };

  const requestOtp = async () => {
    setState({ ...state, loading: true });
    if (doValidate()) {
      try {
        const response = await Auth.forgotPassword(state.data.email);
        console.log(response);
        setState({ ...state, loading: false, receivedOtp: true });
        success("OTP sent successfully");
      } catch (err) {
        let error = parseAuthError(err);
        showError(error);
        setState({ ...state, loading: false, receivedOtp: false });
        console.log(error);
      }
    } else {
      setState({ ...state, loading: false });
    }
  };

  const verifyOtp = async () => {
    if (validateOtpAndPassword()) {
      setState({ ...state, loading: true });
      try {
        const cognitoUser = await Auth.forgotPasswordSubmit(
          state.data.email,
          state.data.inputOtp,
          state.data.confirmPassword
        );
        setState({ ...state, loading: false, receivedOtp: true });
        success("Password changed successfully!");
        console.log(cognitoUser);
        navigate(`/login`);
      } catch (err) {
        let error = parseAuthError(err);
        showError(error);
        console.log(error);
        setState({ ...state, loading: false, receivedOtp: true });
      }
    }
  };

  const handleSubmit = () => {
    if (state.receivedOtp) {
      verifyOtp();
    } else {
      requestOtp();
    }
  };

  const handlePressEnter = (e) => {
    if (e.keyCode === 13) {
      handleSubmit();
    }
  };

  const handleResendOtp = () => {
    if (counter === 0) {
      setCounter(30);
      requestOtp();
    }
  };

  // Reset Password Screen (after OTP received)
  if (state.receivedOtp) {
    return (
      <AuthLayout>
        <BackHeader
          title="Reset Password"
          onBack={() => setState({ ...state, receivedOtp: false, data: { ...state.data, inputOtp: "", password: "", confirmPassword: "" } })}
        />

        <p className="auth-description">
          Enter the OTP sent to {state.data.email} and set your new password.
        </p>

        {/* OTP Input */}
        <div style={{ marginBottom: "16px" }}>
          <label className="auth-form__label" style={{ marginBottom: "8px", display: "block" }}>OTP</label>
          <OTPInput
            value={state.data.inputOtp}
            onChange={(otp) => {
              setState({
                ...state,
                data: { ...state.data, inputOtp: otp },
                errors: { ...state.errors, inputOtp: "" },
              });
            }}
            numInputs={6}
            error={state.errors.inputOtp}
          />
        </div>

        {/* New Password */}
        <div className="auth-form__group">
          <label className="auth-form__label">New Password</label>
          <div className="auth-form__password-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              className={`auth-form__input ${state.errors.password ? "auth-form__input--error" : ""}`}
              placeholder="Enter new password"
              value={state.data.password}
              onKeyDown={handlePressEnter}
              onChange={(e) => {
                setState({
                  ...state,
                  data: { ...state.data, password: e.target.value },
                  errors: { ...state.errors, password: "" },
                });
              }}
            />
            <button
              type="button"
              className="auth-form__password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <Icon
                icon={showPassword ? "mdi-light:eye" : "mdi-light:eye-off"}
                width="20"
                height="20"
              />
            </button>
          </div>
          {state.errors.password ? (
            <span className="auth-form__error">{state.errors.password}</span>
          ) : (
            <span className="auth-form__hint">
              Password must contain a minimum of 6 characters, 1 uppercase, 1 lowercase, 1 number & 1 special character
            </span>
          )}
        </div>

        {/* Confirm Password */}
        <div className="auth-form__group">
          <label className="auth-form__label">Confirm Password</label>
          <div className="auth-form__password-wrapper">
            <input
              type={showConfirmPassword ? "text" : "password"}
              className={`auth-form__input ${state.errors.confirmPassword ? "auth-form__input--error" : ""}`}
              placeholder="Confirm new password"
              value={state.data.confirmPassword}
              onKeyDown={handlePressEnter}
              onChange={(e) => {
                setState({
                  ...state,
                  data: { ...state.data, confirmPassword: e.target.value },
                  errors: { ...state.errors, confirmPassword: "" },
                });
              }}
            />
            <button
              type="button"
              className="auth-form__password-toggle"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
            >
              <Icon
                icon={showConfirmPassword ? "mdi-light:eye" : "mdi-light:eye-off"}
                width="20"
                height="20"
              />
            </button>
          </div>
          {state.errors.confirmPassword && (
            <span className="auth-form__error">{state.errors.confirmPassword}</span>
          )}
        </div>

        {/* Reset Password Button */}
        <button
          className="auth-btn"
          onClick={handleSubmit}
          disabled={state.loading}
          style={{ marginTop: "16px" }}
        >
          {state.loading ? <span className="auth-spinner"></span> : "Reset Password"}
        </button>

        {/* Resend OTP */}
        <p className="auth-resend">
          Didn't receive an OTP?{" "}
          <span
            className={`auth-resend__link ${counter !== 0 ? "auth-resend__link--disabled" : ""}`}
            onClick={handleResendOtp}
          >
            Resend OTP {counter !== 0 ? `in ${counter} seconds` : ""}
          </span>
        </p>
      </AuthLayout>
    );
  }

  // Request OTP Screen (initial)
  return (
    <AuthLayout>
      <BackHeader
        title="Forgot Password"
        onBack={() => navigate("/login")}
      />

      <p className="auth-description">
        Enter your email address and we'll send you an OTP to reset your password.
      </p>

      {/* Email Input */}
      <div className="auth-form__group">
        <label className="auth-form__label">Email</label>
        <input
          type="email"
          className={`auth-form__input ${state.errors.email ? "auth-form__input--error" : ""}`}
          placeholder="Enter your email"
          value={state.data.email}
          onKeyDown={handlePressEnter}
          onChange={(e) => {
            setState({
              ...state,
              data: { ...state.data, email: e.target.value.toLowerCase() },
              errors: { ...state.errors, email: "" },
            });
          }}
          onBlur={() => {
            if (state.data.email === undefined || state.data.email === "") {
              setState({
                ...state,
                errors: { ...state.errors, email: "Please enter your email" },
              });
            } else if (!isValidEmail(state.data.email)) {
              setState({
                ...state,
                errors: { ...state.errors, email: "Please enter a valid email address" },
              });
            }
          }}
        />
        {state.errors.email && (
          <span className="auth-form__error">{state.errors.email}</span>
        )}
      </div>

      {/* Send OTP Button */}
      <button
        className="auth-btn"
        onClick={handleSubmit}
        disabled={state.loading}
        style={{ marginTop: "24px" }}
      >
        {state.loading ? <span className="auth-spinner"></span> : "Send OTP"}
      </button>

      {/* Back to Sign In */}
      <p className="auth-footer-text">
        Remember your password?{" "}
        <span
          role="button"
          onClick={() => navigate("/login")}
        >
          Sign In
        </span>
      </p>
    </AuthLayout>
  );
};

export default ForgotPassword;
